import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import dayjs from 'dayjs';
import { useQueryClient, useMutation, useQuery } from 'react-query';
import Alert from '@mui/material/Alert';
import {
	UpdateAshlinkUserTermDate,
	UpdateAshlinkChildUserInfo,
	GetServiceLocations,
	GetAvailableServiceLocations,
} from '../../data/apiCalls';
import LoadingButton from '../shared/LoadingButton';
import useSessionContext from '../../data/queryHooks/useSessionContext';
import SubAccountLocationsTable from './shared/SubAccountLocationsTable';

const EditSubAccounts = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const queryClient = useQueryClient();

	const {
		ashlinkUserEntityType,
		ashlinkUserEntityStatus,
		providerId,
		clinicId,
		ashlinkUserId,
		userInfo,
	} = useSessionContext();

	const {
		id: childAshlinkUserId,
		email,
		firstName,
		lastName,
		ashlinkUserLevel: childAshlinkUserLevel,
		termDate,
	} = location.state || {};

	// state for locking and selecting all locations when user level is 1
	const [ashlinkUserLevel, setAshlinkUserLevel] = useState(
		childAshlinkUserLevel || 3
	);

	// Associated locations state
	const [removeSelection, setRemoveSelection] = useState([]);
	const [
		associatedLocationsSearchString,
		setAssociatedLocationsSearchString,
	] = useState('');
	const [associatedLocationsPage, setAssociatedLocationsPage] = useState(0);
	const [associatedLocationsPageSize, setAssociatedLocationsPageSize] =
		useState(10);

	const {
		data: associatedLocationsData,
		isLoading: isLoadingAssociatedLocations,
	} = useQuery({
		queryKey: [
			'AssociatedLocations',
			ashlinkUserId,
			childAshlinkUserId,
			providerId,
			ashlinkUserEntityType,
			clinicId,
			associatedLocationsSearchString || '',
			associatedLocationsPage || 0,
			associatedLocationsPageSize || 10,
		],
		queryFn: () =>
			GetServiceLocations(
				ashlinkUserEntityType,
				childAshlinkUserId,
				providerId,
				clinicId,
				associatedLocationsSearchString,
				associatedLocationsPage + 1, // endpoint is 1 indexed, but data grid is 0 indexed
				associatedLocationsPageSize ?? 10 // default to 10 if pageSize is not set (this is the data grid default)
			),
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		keepPreviousData: true,
	});

	// Available locations state

	const [addSelection, setAddSelection] = useState([]);
	const [availableLocationsSearchString, setAvailableLocationsSearchString] =
		useState('');
	const [availableLocationsPage, setAvailableLocationsPage] = useState(0);
	const [availableLocationsPageSize, setAvailableLocationsPageSize] =
		useState(10);

	const {
		data: availableLocationsData,
		isLoading: isLoadingAvailableLocations,
	} = useQuery({
		queryKey: [
			'AvailableLocations',
			ashlinkUserId,
			childAshlinkUserId,
			ashlinkUserEntityType,
			providerId,
			clinicId,
			availableLocationsSearchString || '',
			availableLocationsPage || 0,
			availableLocationsPageSize || 10,
		],
		queryFn: () =>
			GetAvailableServiceLocations(
				ashlinkUserId,
				childAshlinkUserId,
				ashlinkUserEntityType,
				providerId,
				clinicId,
				availableLocationsSearchString,
				availableLocationsPage + 1, // endpoint is 1 indexed, but data grid is 0 indexed
				availableLocationsPageSize ?? 10 // default to 10 if pageSize is not set (this is the data grid default)
			),
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		keepPreviousData: true,
	});

	// Terminate and Reactivate Modal State
	const [terminated, setTerminated] = useState(!!termDate);
	const [terminateModalOpen, setTerminateModalOpen] = useState(false);
	const [reactivateModelOpen, setReactivateModelOpen] = useState(false);
	const [alertText, setAlertText] = useState({
		title: '',
		description: '',
	});
	const [reactivateAlert, setReactivateAlert] = useState({
		title: '',
		description: '',
		severity: '',
	});

	// Confirm Sub Account Changes
	const { mutate: updateChildUserInfo, isLoading } = useMutation(
		params => UpdateAshlinkChildUserInfo(params),
		{
			onSuccess: () => {
				queryClient.invalidateQueries('AssociatedLocations');
				queryClient.invalidateQueries('AvailableLocations');
				queryClient.invalidateQueries('SubAccount');
				sessionStorage.setItem('isConfirmed', true);
				navigate('/account/manageSubAccounts', {
					state: { email },
				});
			},
			onError: e => {
				if (e?.response?.data?.SubResultCode === 11039) {
					setAlertText({
						title: 'No Selection Made',
						description: 'Please select at least one location',
					});
				} else {
					setAlertText({
						title: 'Confirm Sub Account Failed',
						description:
							'Sorry, something went wrong while trying to process your request.',
					});
				}
			},
		}
	);

	const confirmSubAccountSubmit = () => {
		if (
			//No Changes Made
			!removeSelection.length && // No locations removed
			!addSelection.length && // No locations added
			ashlinkUserLevel === childAshlinkUserLevel // No level change
		) {
			setAlertText({
				title: 'No Changes Made',
				description: 'Please make changes to the sub account',
			});
		} else if (
			//No Locations Selected
			removeSelection.length ===
				associatedLocationsData?.data?.data?.totalRows && // all locations removed
			!addSelection.length // No locations added)
		) {
			setAlertText({
				title: 'No Locations Selected',
				description: 'Please select at least one location',
			});
		} else {
			const params = {
				AshlinkUserId: childAshlinkUserId,
				AshlinkUserLevel: Number(ashlinkUserLevel),
				AshlinkUserEntityType: ashlinkUserEntityType,
				AshlinkUserEntityStatus: ashlinkUserEntityStatus,
				ProviderID: providerId,
				ClinicId: clinicId,
				DrFedIdsRemoved: ashlinkUserLevel === 1 ? [] : removeSelection, // If user level is 1, no locations will be removed
				DrFedIdsAdded: ashlinkUserLevel === 1 ? [] : addSelection, // If user level is 1, all locations will be added automatically in backend
			};
			updateChildUserInfo(params);
		}
	};

	// Terminate Sub Account
	const { mutate: terminateSubAccount, isLoading: isLoadingTerminate } =
		useMutation(params => UpdateAshlinkUserTermDate(params), {
			onSuccess: () => {
				queryClient.invalidateQueries('SubAccount');
				sessionStorage.setItem('isTerm', true);
				navigate('/account/manageSubAccounts', {
					state: { email },
				});
			},
			onError: () => {
				setAlertText({
					title: 'Terminate Account Failed',
					description:
						'There was an error while terminating the sub account',
				});
			},
		});

	const terminateSubmit = async () => {
		if (terminateModalOpen) setTerminateModalOpen(false);
		if (alertText?.title)
			setAlertText({
				title: '',
				description: '',
			});

		const params = {
			AshlinkUserId: childAshlinkUserId,
			TerminationDate: dayjs().format('YYYY-MM-DD'),
		};

		terminateSubAccount(params);
	};

	// Reactivate Sub Account
	const { mutate: reactivateSubAccount, isLoading: isLoadingReactivate } =
		useMutation(params => UpdateAshlinkUserTermDate(params), {
			onSuccess: () => {
				navigate(location, {
					replace: true,
					state: {
						id: childAshlinkUserId,
						email,
						firstName,
						lastName,
						ashlinkUserLevel: childAshlinkUserLevel,
						termDate: null,
					},
				});
				queryClient.invalidateQueries('SubAccount');
				setTerminated(false);
				setReactivateAlert({
					title: 'Sub-Account Successfully Re-Activated',
					description: `${email} has been re-activated`,
					severity: 'success',
				});
			},
			onError: () => {
				setReactivateAlert({
					title: 'Reactivation Failed!',
					description:
						'There was an error while reactivating the sub account.',
					severity: 'error',
				});
			},
		});

	const reactivateSubmit = async () => {
		if (reactivateModelOpen) setReactivateModelOpen(false);
		if (reactivateAlert?.title)
			setReactivateAlert({
				title: '',
				description: '',
				severity: '',
			});

		const params = {
			AshlinkUserId: childAshlinkUserId,
		};

		reactivateSubAccount(params);
	};

	// Automatically select all locations when user level is 1
	useEffect(() => {
		if (
			Number(ashlinkUserLevel) === 1 &&
			availableLocationsData?.data?.data?.ashlinkUserLocationByEntity
		) {
			const allLocationIds =
				availableLocationsData.data.data.ashlinkUserLocationByEntity
					.map(
						location =>
							location.id || location.drFedId || location.DrFedId
					)
					.filter(Boolean);
			setAddSelection(allLocationIds);
		}
	}, [ashlinkUserLevel, availableLocationsData]);

	// Clear error text when change is made
	useEffect(() => {
		if (
			alertText.title &&
			(removeSelection.length || addSelection.length)
		) {
			setAlertText({
				title: '',
				description: '',
			});
		}
	}, [removeSelection, addSelection]);

	// Clear queries on unmount
	useEffect(
		() => () => {
			queryClient.removeQueries('AssociatedLocations');
			queryClient.removeQueries('AvailableLocations');
		},
		[]
	);

	// Scroll to reactivate alert when displayed
	const reactivateAlertRef = useRef(null);
	useEffect(() => {
		if (reactivateAlert?.title) {
			reactivateAlertRef.current?.scrollIntoView({ behavior: 'smooth' });
			reactivateAlertRef.current?.focus();
		}
	}, [reactivateAlert?.title]);

	return (
		<Paper elevation={0}>
			<Container sx={{ py: 2 }}>
				<Typography variant='h5' component='h1' mb={2}>
					Edit Sub Account
				</Typography>
				<Divider sx={{ mb: 2 }} />
				<Typography>Edit the sub account information below</Typography>
				{reactivateAlert?.title && (
					<Alert
						id='reactivate-alert'
						severity={reactivateAlert.severity}
						sx={{ mb: 3 }}
						onClose={() =>
							setReactivateAlert({
								title: '',
								description: '',
								severity: '',
							})
						}
						ref={reactivateAlertRef}
						tabIndex={-1}
					>
						<Typography fontWeight={700}>
							{reactivateAlert.title}
						</Typography>
						<Typography>{reactivateAlert.description}</Typography>
					</Alert>
				)}
				<Typography fontWeight='fontWeightBold' mt={2}>
					Sub Account&apos;s Name:
				</Typography>
				<Typography data-testid='sub-account-name'>
					{`${firstName ?? ''} ${lastName ?? ''} `.trim()}
				</Typography>
				<Typography fontWeight='fontWeightBold' mt={2}>
					Sub Account&apos;s Email Address:
				</Typography>
				<Typography data-testid='sub-account-email'>{email}</Typography>
				<SubAccountLocationsTable
					pageTitle='Locations Associated With Sub Account'
					locations={
						associatedLocationsData?.data?.data
							?.ashlinkUserProfiles ?? []
					}
					isLoading={isLoadingAssociatedLocations}
					inverse
					selectionModel={
						ashlinkUserLevel === 1 ? [] : removeSelection
					}
					selectionModelUpdate={selections =>
						setRemoveSelection(selections)
					}
					locked={ashlinkUserLevel === 1 || terminated}
					allChecked={ashlinkUserLevel === 1}
					// error={touched.locations && errors.locations}
					page={associatedLocationsPage}
					pageSize={associatedLocationsPageSize}
					onPageChange={newPage =>
						setAssociatedLocationsPage(newPage)
					}
					totalRows={
						associatedLocationsData?.data?.data?.totalRows ?? 0
					}
					onPageSizeChange={newPageSize => {
						setAssociatedLocationsPageSize(newPageSize);
					}}
					onSearchStrChange={newSearchStr =>
						setAssociatedLocationsSearchString(newSearchStr)
					}
				/>

				<SubAccountLocationsTable
					pageTitle='Select Location:'
					locations={
						availableLocationsData?.data?.data
							?.ashlinkUserLocationByEntity ?? []
					}
					isLoading={isLoadingAvailableLocations}
					selectionModel={ashlinkUserLevel === 1 ? [] : addSelection}
					selectionModelUpdate={selections =>
						setAddSelection(selections)
					}
					locked={ashlinkUserLevel === 1 || terminated}
					allChecked={ashlinkUserLevel === 1}
					// error={touched.locations && errors.locations}
					page={availableLocationsPage}
					pageSize={availableLocationsPageSize}
					onPageChange={newPage => setAvailableLocationsPage(newPage)}
					totalRows={
						availableLocationsData?.data?.data?.totalRows ?? 0
					}
					onPageSizeChange={newPageSize => {
						setAvailableLocationsPageSize(newPageSize);
					}}
					onSearchStrChange={newSearchStr =>
						setAvailableLocationsSearchString(newSearchStr)
					}
				/>

				<Box sx={{ mt: 6 }}>
					<Typography variant='h6' component='h2'>
						Select the ASHLink Access Level:
					</Typography>
					<RadioGroup
						defaultValue={ashlinkUserLevel}
						value={ashlinkUserLevel}
						onChange={event =>
							setAshlinkUserLevel(Number(event.target.value))
						}
					>
						<FormControlLabel
							value={1}
							control={<Radio />}
							disabled={terminated}
							label={
								<div>
									<Typography>
										<strong>Level 1: </strong>
										Users are granted full access to all
										features of ASHLink with the exception
										of{' '}
										{[400, 401, 402, 500].includes(
											userInfo.clubTypeID
										)
											? 'quarterly validations and see '
											: 'seeing '}
										alerts and warnings.
									</Typography>
								</div>
							}
						/>
						<FormControlLabel
							value={2}
							control={<Radio />}
							disabled={terminated}
							label={
								<div>
									<Typography>
										<strong>Level 2: </strong>
										Same permissions as Level 1 - With the
										exception of inviting and creating sub
										accounts.
									</Typography>
								</div>
							}
						/>
						<FormControlLabel
							value={3}
							control={<Radio />}
							disabled={terminated}
							label={
								<div>
									<Typography>
										<strong>Level 3: </strong>
										User access is limited to checking
										eligibility
									</Typography>
								</div>
							}
						/>
					</RadioGroup>
				</Box>

				<Box
					sx={{
						mt: 6,
					}}
				>
					<Typography
						sx={{
							fontStyle: 'italic',
							mt: 6,
							mb: 3.5,
						}}
					>
						If you have any questions, please select
						&lsquo;Help&lsquo; located on the blue task bar above
						and &lsquo;Submit an online Question or Comment&lsquo;.
						Responses can be viewed in your &lsquo;Inbox&lsquo;.
					</Typography>
				</Box>

				<Stack
					direction='row'
					sx={{
						justifyContent: 'space-between',
					}}
				>
					<Box>
						{!terminated && (
							<LoadingButton
								variant='contained'
								sx={{
									mr: 2,
								}}
								onClick={() => confirmSubAccountSubmit()}
								isLoading={isLoading}
							>
								Confirm Sub Account
							</LoadingButton>
						)}
						<Button
							variant='outlined'
							onClick={() =>
								navigate('/account/manageSubAccounts')
							}
						>
							Cancel
						</Button>
					</Box>
					{terminated ? (
						<LoadingButton
							variant='contained'
							onClick={() => setReactivateModelOpen(true)}
							isLoading={isLoadingReactivate}
						>
							Re-activate This Account
						</LoadingButton>
					) : (
						<LoadingButton
							variant='contained'
							color='error'
							onClick={() => setTerminateModalOpen(true)}
							isLoading={isLoadingTerminate}
						>
							Terminate This Account
						</LoadingButton>
					)}
				</Stack>
			</Container>

			{/* Terminate Model */}
			<Dialog open={terminateModalOpen} fullWidth>
				<DialogTitle id='alert-dialog-title'>
					Terminate Account
				</DialogTitle>

				<DialogContent>
					<DialogContentText>
						Are you sure you want to terminate this account?
					</DialogContentText>
					<Table style={{ borderCollapse: 'collapse' }}>
						<TableBody>
							<TableRow>
								<TableCell
									style={{
										border: 'none',
										fontWeight: 'bold',
									}}
								>
									First Name
								</TableCell>
								<TableCell
									style={{
										border: 'none',
										fontWeight: 'bold',
									}}
								>
									Last Name
								</TableCell>
								<TableCell
									style={{
										border: 'none',
										fontWeight: 'bold',
									}}
								>
									Email
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell
									data-testid='sub-account-modal-firstname'
									style={{ border: 'none' }}
								>
									{firstName}
								</TableCell>
								<TableCell
									data-testid='sub-account-modal-lastname'
									style={{ border: 'none' }}
								>
									{lastName}
								</TableCell>
								<TableCell
									data-testid='sub-account-modal-email'
									style={{ border: 'none' }}
								>
									{email}
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</DialogContent>

				<DialogActions>
					<Button
						variant='outlined'
						onClick={() => setTerminateModalOpen(false)}
					>
						Cancel
					</Button>
					<Button
						variant='contained'
						color='error'
						onClick={() => terminateSubmit()}
					>
						Terminate Account
					</Button>
				</DialogActions>
			</Dialog>

			{/* Reactivate Model */}
			<Dialog open={reactivateModelOpen} fullWidth>
				<DialogTitle id='alert-dialog-title'>
					Reactivate Account
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to reactivate this account?
					</DialogContentText>
					<Table style={{ borderCollapse: 'collapse' }}>
						<TableBody>
							<TableRow>
								<TableCell
									style={{
										border: 'none',
										fontWeight: 'bold',
									}}
								>
									First Name
								</TableCell>
								<TableCell
									style={{
										border: 'none',
										fontWeight: 'bold',
									}}
								>
									Last Name
								</TableCell>
								<TableCell
									style={{
										border: 'none',
										fontWeight: 'bold',
									}}
								>
									Email
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell style={{ border: 'none' }}>
									{firstName}
								</TableCell>
								<TableCell style={{ border: 'none' }}>
									{lastName}
								</TableCell>
								<TableCell style={{ border: 'none' }}>
									{email}
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</DialogContent>
				<DialogActions>
					<Button
						variant='outlined'
						color='secondary'
						onClick={() => setReactivateModelOpen(false)}
					>
						Cancel
					</Button>
					<Button
						variant='contained'
						color='success'
						onClick={() => reactivateSubmit()}
					>
						Reactivate Account
					</Button>
				</DialogActions>
			</Dialog>

			{alertText?.title && (
				<Alert
					severity='error'
					sx={{ mb: 3 }}
					onClose={() => setAlertText({ title: '', description: '' })}
				>
					<Typography fontWeight={700}>{alertText.title}</Typography>
					<Typography>{alertText.description}</Typography>
				</Alert>
			)}
		</Paper>
	);
};

export default EditSubAccounts;
